import { Box, Button, useTheme } from '@mui/material';
import cn from 'classnames';
import isEqual from 'lodash/isEqual';
import * as React from 'react';

import { ChevronLeft as ChevronLeftIcon } from '@mui/icons-material';

import { BackButton, Drawer, UnsavedChangesModal } from '../../components';
import useModal from '../../hooks/useModal';
import useWindowSize from '../../hooks/useWindowSize';
import { IReportEngagementItem } from '../../types/reports';
import { DrawerAnimationDirectionEnum } from '../../types/shell';
import AddSendItemsContainer, { IAddItemsRef } from './AddSendItemsContainer/AddSendItemsContainer';

import styles from './AddSendItemsSidebar.module.scss';

interface IAddSendItemsSidebarProps {
  onClose: () => void;
  reportItems?: IReportEngagementItem[] | null;
  isShown?: boolean;
  status?: string;
  onSubmit?: (items: IReportEngagementItem[] | undefined) => void;
  isDirectSend?: boolean;
}

const AddSendItemsSidebar: React.FC<IAddSendItemsSidebarProps> = ({
  reportItems,
  isShown,
  status,
  onSubmit,
  onClose,
  isDirectSend,
}) => {
  const theme = useTheme();
  const { sass: variables } = useTheme();
  const { mobile, HD } = useWindowSize();
  const { openModal: openSaveChangesModal, closeModal: closeSaveChangesModal, Modal: SaveChangesModal } = useModal();

  const itemsRef = React.useRef<IAddItemsRef>(null);
  const [isValueChanged, setIsValueChanged] = React.useState(false);

  const from = React.useMemo(() => (mobile ? variables.boxSidebarMinHeight : variables.boxSidebarMinWidth), [mobile]);
  const to = React.useMemo(() => {
    if (mobile) {
      return variables.boxSidebarMaxHeight;
    }

    if (HD) {
      return variables.boxSidebarMaxWidthHD;
    }

    return variables.boxSidebarMaxWidthFullHD;
  }, [mobile, HD]);

  const handleItemsSubmit = React.useCallback(() => {
    if (typeof onSubmit === 'function') {
      onSubmit(itemsRef.current?.getValues());
    }
  }, [onSubmit]);

  const saveChangesModalActions = React.useMemo(() => {
    return [
      { title: 'Save and close', onClick: handleItemsSubmit },
      {
        title: 'Discard changes',
        onClick: () => {
          if (onClose) {
            onClose();
          }
        },
        isOutlined: true,
      },
    ];
  }, [onClose, handleItemsSubmit]);

  const saveChangesModal = React.useMemo(
    () => (
      <SaveChangesModal
        overlayClassName={styles.saveChangesModalOverlay}
        className={cn(styles.saveChangesModal, 'common-modal')}
      >
        {() => <UnsavedChangesModal actions={saveChangesModalActions} onClose={closeSaveChangesModal} />}
      </SaveChangesModal>
    ),
    [closeSaveChangesModal, saveChangesModalActions, SaveChangesModal],
  );

  const handleCloseSidebar = React.useCallback(() => {
    if (itemsRef.current?.getValues() && !isEqual(itemsRef.current?.getValues(), reportItems)) {
      return openSaveChangesModal();
    }

    if (typeof onClose === 'function') {
      return onClose();
    }
  }, [openSaveChangesModal, onClose, reportItems]);

  React.useEffect(() => {
    const interval = setInterval(() => {
      if (itemsRef.current) {
        setIsValueChanged(itemsRef.current.isValueChanged);
      }
    }, 100);
    return () => clearInterval(interval);
  }, []);

  const buttonStyles = { fontSize: '15px', lineHeight: 1.7, py: 1, px: 2.75 };

  return (
    <React.Fragment>
      <Drawer
        className={cn(styles.container)}
        direction={mobile ? DrawerAnimationDirectionEnum.Vertically : DrawerAnimationDirectionEnum.Horizontally}
        from={from}
        to={to}
        trigger={!!isShown}
        onClose={handleCloseSidebar}
        withOverlay
        overlayClassName={styles.overlay}
      >
        {isShown && (
          <div className={styles.contentContainer}>
            <div className={styles.closeButtonContainer}>
              <button className={styles.closeButton} onClick={handleCloseSidebar} />
            </div>
            <div className={styles.titleContainer}>
              <BackButton className={styles.back} onClick={handleCloseSidebar} />
              <div className={styles.title}>Add Items</div>
            </div>
            <div className={styles.content}>
              <AddSendItemsContainer
                reportItems={reportItems}
                ref={itemsRef}
                canRemoveAll={false}
                onSubmit={handleItemsSubmit}
                isShown={isShown}
                onCancel={handleCloseSidebar}
                status={status}
                isDirectSend={isDirectSend}
              />
            </div>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                position: 'sticky',
                bottom: 0,
                paddingRight: 3,
                pt: 2,
                pb: 2,
                backgroundColor: theme.palette.common.white,
              }}
            >
              <Box>
                {isValueChanged && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleCloseSidebar}
                    data-action="back_btn"
                    sx={buttonStyles}
                    startIcon={<ChevronLeftIcon />}
                  >
                    Back
                  </Button>
                )}
              </Box>
              <Box>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleItemsSubmit}
                  disabled={!isValueChanged}
                  data-action="apply_changes_btn"
                  sx={buttonStyles}
                >
                  Apply changes
                </Button>
              </Box>
            </Box>
          </div>
        )}
      </Drawer>
      {saveChangesModal}
    </React.Fragment>
  );
};

export default AddSendItemsSidebar;
